"use client";

// Stats com contador animado disparado por IntersectionObserver.
// Recebe `indicators` por prop (vindo de home.indicators) — não usa
// useSiteData(), pra que o componente pai possa ser Server Component
// e essa ilha client seja a única coisa hidratada nessa seção.

import { useEffect, useMemo, useRef, useState } from "react";
import { parsePtBrNumber } from "../lib/mbm-helpers";

export default function AnimatedStats({ indicators }) {
  const ref = useRef(null);
  const frameRef = useRef(null);
  const startedRef = useRef(false);
  const [progress, setProgress] = useState(0);

  const items = useMemo(
    () =>
      (Array.isArray(indicators) ? indicators : []).map((s) => ({
        value: parsePtBrNumber(s.value),
        suffix: s.suffix ?? "",
        label: s.label ?? "",
      })),
    [indicators]
  );

  useEffect(() => {
    if (items.length === 0) return undefined;
    startedRef.current = false;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const startCount = () => {
      if (startedRef.current) return;
      startedRef.current = true;

      if (prefersReducedMotion) {
        setProgress(1);
        return;
      }

      const duration = 1400;
      const start = performance.now();

      const tick = (now) => {
        const elapsed = now - start;
        const linear = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - linear, 3);
        setProgress(eased);

        if (linear < 1) {
          frameRef.current = requestAnimationFrame(tick);
        }
      };

      frameRef.current = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startCount();
          observer.disconnect();
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px -8% 0px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [items]);

  if (items.length === 0) return null;

  const formatValue = (value) => value.toLocaleString("pt-BR");

  return (
    <div className="stats" ref={ref}>
      {items.map((stat, index) => (
        <div
          className="stat fade-up"
          key={stat.label || index}
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div className="v">
            {formatValue(Math.round(stat.value * progress))}
            {stat.suffix && <sup>{stat.suffix}</sup>}
          </div>
          <div className="l">{stat.label}</div>
        </div>
      ))}
    </div>
  );
}
